// Central base-URL resolver for the CKN Business service APIs consumed by BrandOS.
//
// ckn.business is split by project/service prefix, not by the older
// customer/inventory/sales grouping. Browser calls default to same-origin proxy paths
// (`/api/<service>`). Deployments may override any service with
// `window.__CKN_API_BASE__ = { orders: "...", warehouse: "...", ... }`.
export const BUSINESS_SERVICES = [
    "accounting",
    "content",
    "core",
    "customer",
    "dispatching",
    "inventory",
    "orders",
    "production",
    "promotions",
    "purchasing",
    "transportation",
    "warehouse",
];
/**
 * Base for a service — a SAME-ORIGIN proxy path. The browser calls brandos at
 * `/api/<domain>/…`; the brandos server reverse-proxies that to the real service
 * (upstream taken from the service's *_API_URL env var — see src/index.ts). Keeping
 * every API call same-origin means the browser never does a cross-origin request, so
 * there is no CORS, no cross-origin CSP, and no cross-host browser blocking to fight.
 * Configure WHERE the services live in the brandos server env
 * (ORDERS_API_URL, WAREHOUSE_API_URL, CUSTOMER_API_URL, ...), not here.
 */
export function serviceBase(domain) {
    const bases = globalThis.__CKN_API_BASE__;
    const configured = bases && typeof bases[domain] === "string" ? bases[domain].trim() : "";
    if (configured)
        return configured.replace(/\/+$/, "");
    return `/api/${domain}`;
}
/**
 * Route-scoped branch map: url-path prefix → the branch that path family sells for.
 * The staff terminals (/staff_popup, /staff_flagship) are NOT in this map — they get their
 * own default via `STAFF_TERMINAL_DEFAULT_BRANCH` below, still overridable per load via
 * `?branch=<CODE>` (see storefrontBranch). The customer mini-app (/miniapp) and the pop-up
 * POS (/pos) resolve to a fixed branch here; the server injects the same map from env
 * (MINIAPP_BRANCH / POS_BRANCH) via the theme head, and this built-in default is the
 * fallback (and in Node/test where there is no `location`). The deployment default is
 * `__CKN_BRANCH__` (STOREFRONT_BRANCH env, GAYSORN).
 */
// DEFERRED, NOT MISSED. The four branch codes below and the `"GAYSORN"` in storefrontBranch()
// are maison KEEPS's stores, and this file is shared by every tenant. They are LAST-RESORT
// fallbacks only: the server injects `__CKN_BRANCH__` and `__CKN_BRANCH_ROUTES__` per tenant from
// helpers/tenant-branch.ts on every page, so a signed-in tenant never reaches these. Removing them
// means giving the two staff terminals their own per-tenant configuration keys and seeding them
// before the change ships, or the tills open on no branch at all — that belongs with the rest of
// the consumer-surface de-branding (POS, mini-app, member app), not here. Allow-listed in
// test/tenant-neutral-console.test.mjs with this note as the reason.
const DEFAULT_BRANCH_ROUTES = {
    "/miniapp": "SIAMCENTER",
    // The pop-up POS terminal + customer mirror sell the SIAMCENTER store (same second
    // branch the mini-app books into) — so the cashier's queue, catalog and stock all
    // resolve to the SIAMCENTER orders the mini-app created. Covers /pos, /pos/order,
    // /pos/payment and /pos/mirror via the prefix match below. The server injects the same
    // map from env POS_BRANCH (theme.imb.pos.ejs); this is the Node/test fallback.
    "/pos": "SIAMCENTER",
};
/**
 * Each staff terminal TYPE sells exactly one store: /staff_popup is the Gaysorn pop-up,
 * /staff_flagship is the Siam Center flagship (see staffv2app.ts's own docs). `?branch=`
 * can still override this for testing, and the topbar dropdown can still persist a pick to
 * localStorage — but with NEITHER present, each terminal must open on ITS OWN store, not
 * on a shared deployment-wide default. Before this map existed both terminals fell through
 * to the same `window.__CKN_BRANCH__` (STOREFRONT_BRANCH, "GAYSORN") — so a fresh visit to
 * /staff_flagship with no query string and no prior localStorage pick silently opened on
 * Gaysorn instead of Siam Center.
 */
const STAFF_TERMINAL_DEFAULT_BRANCH = {
    "/staff_popup": "GAYSORN",
    "/staff_flagship": "SIAMCENTER",
};
/**
 * The deployment's own branch — `window.__CKN_BRANCH__` (server env STOREFRONT_BRANCH), else
 * "GAYSORN" — WITHOUT the per-route map `storefrontBranch()` applies.
 *
 * For settings that belong to the BRAND rather than to a shop, read from the storefront config
 * row keyed on this scope. Registration rules are the case in point: Membership › Environment
 * reads and publishes them under `storefrontBranch()`, which on an admin page (no route prefix)
 * IS this value, and the member app reads them the same way. The POS customer display lives
 * under `/pos`, where `storefrontBranch()` answers SIAMCENTER — a scope with no row of its own —
 * so it read a different row (or none), and nothing an admin changed on the Environment screen
 * reached the counter's sign-up form.
 *
 * Only for brand-level config. Anything about stock, orders or queues must keep using
 * `storefrontBranch()` — that is the shop the till actually sells for.
 */
export function storefrontBaseBranch() {
    const b = globalThis.__CKN_BRANCH__;
    return (typeof b === "string" && b.trim() !== "") ? b.trim() : "GAYSORN";
}
/**
 * The physical branch the CURRENT app sells for (e.g. "GAYSORN"). Resolved per-route:
 * apps under a prefix in `__CKN_BRANCH_ROUTES__` (mini-app) sell that branch; each staff
 * terminal (/staff_popup, /staff_flagship) sells its own store per
 * `STAFF_TERMINAL_DEFAULT_BRANCH`; everything else falls back to the deployment default
 * `window.__CKN_BRANCH__` (injected by the theme head from server env STOREFRONT_BRANCH).
 * A branch-scoped storefront passes this to the catalog + stock + order queries so it only
 * ever touches ITS branch's inventory. Defaults to "GAYSORN".
 *
 * Reading the live `location.pathname` (rather than a per-app constant) scopes EVERY
 * call site at once — including the panes that call storefrontBranch() directly
 * (staff-pick, stockroom-pick, checkout, inventory) — with no per-file wiring.
 */
export function storefrontBranch() {
    const base = storefrontBaseBranch();
    const routes = globalThis.__CKN_BRANCH_ROUTES__ || DEFAULT_BRANCH_ROUTES;
    try {
        const path = globalThis.location?.pathname || "";
        const ls = globalThis.localStorage;
        // Staff terminals — pop-up (/staff_popup) + flagship (/staff_flagship). Each terminal
        // TYPE sells one specific store by default (STAFF_TERMINAL_DEFAULT_BRANCH), but
        // `?branch=<CODE>` can still override it (testing / a future multi-store terminal).
        // Resolve order: ?branch= on this load → the per-terminal pick the topbar dropdown
        // persisted (`ckn_staff_branch:/staff_popup` vs `:/staff_flagship`, kept independent
        // though they share one storage origin) → this terminal's own default. onMounted seeds
        // localStorage from ?branch= so the branch survives client-side pane navigation that
        // drops the query string.
        const staffBase = (path === "/staff_flagship" || path.startsWith("/staff_flagship/")) ? "/staff_flagship"
            : (path === "/staff_popup" || path.startsWith("/staff_popup/")) ? "/staff_popup" : "";
        if (staffBase) {
            const qb = new URLSearchParams(globalThis.location?.search || "").get("branch");
            if (qb && qb.trim() !== "")
                return qb.trim().toUpperCase();
            const sel = ls?.getItem("ckn_staff_branch:" + staffBase);
            if (typeof sel === "string" && sel.trim() !== "")
                return sel.trim();
            return STAFF_TERMINAL_DEFAULT_BRANCH[staffBase] ?? base;
        }
        for (const prefix in routes) {
            if (path === prefix || path.startsWith(prefix + "/")) {
                const v = routes[prefix];
                if (typeof v === "string" && v.trim() !== "")
                    return v.trim();
            }
        }
        // Admin Inventory / RFID shells: the topbar branch switcher (themes/imb/public/default.js)
        // persists the user's pick in localStorage. Honour it so every inventory pane loads
        // THAT branch's stock/warehouse. Scoped to /inventory + /rfid (the RFID module reads the
        // same branch scope) so a selection there never leaks into the mini-app / staff terminals,
        // which share the same storage origin.
        if (path === "/inventory" || path.startsWith("/inventory/") || path === "/rfid" || path.startsWith("/rfid/")) {
            const sel = globalThis.localStorage?.getItem("ckn_branch_code");
            if (typeof sel === "string" && sel.trim() !== "")
                return sel.trim();
        }
    }
    catch { /* no location/storage (SSR/Node/test) → deployment default */ }
    return base;
}
/**
 * What to CALL the signed-in tenant, for console copy that is shared by every one of them.
 *
 * Served by `/client-config.js` from `helpers/tenant-brand.ts` — the tenant's own configured
 * brand name, else its `core.company.legal_name`, else "".
 *
 * "" IS THE ANSWER, not a reason to substitute one. The panes that call this had "maison KEEPS"
 * compiled into their copy and showed it to LOOKBOOK and to CKN; a sentence that drops the name
 * when nothing is known reads fine, and a sentence that falls back to a literal is the bug.
 * Render it with `tenantNameOr("")` at the call site rather than defaulting here, so the
 * with-a-name and without-a-name wordings both live where a reader can see them.
 */
export function tenantName() {
    const n = globalThis.__CKN_TENANT_NAME__;
    return typeof n === "string" ? n.trim() : "";
}
/** The signed-in tenant's client_code ("MSK", "LBLB", …), or "" on a surface with no tenant. */
export function tenantCode() {
    const c = globalThis.__CKN_TENANT_CODE__;
    return typeof c === "string" ? c.trim().toUpperCase() : "";
}
/**
 * `<prefix><tenant name><suffix>`, or "" when the tenant has no name — so a caller can write
 * `"aligned to " + tenantName() + " requirements"` as `tenantPhrase(" aligned to ", " requirements")`
 * and get the whole clause, punctuation included, dropped rather than left dangling.
 */
export function tenantPhrase(prefix = "", suffix = "") {
    const n = tenantName();
    return n ? `${prefix}${n}${suffix}` : "";
}
/**
 * Full URL for a service endpoint using its verbatim ckn-business.md contract path.
 */
export function apiPath(domain, path) {
    const p = path.startsWith("/") ? path : `/${path}`;
    return `${serviceBase(domain)}${p}`;
}
/**
 * @deprecated Kept only for panes not yet migrated to the src/apps/api/* layer.
 */
export function apiUrl(domain, pane) {
    return `${serviceBase(domain)}/${domain}/${pane}`;
}
//# sourceMappingURL=apibase.js.map